import { Box, Skeleton, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';

const SupplierPerformanceBody = () => {
  const { supplierData, currency } = useDashboard();
  const { t } = useTranslation();

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toFixed(0);
  };

  const formatTooltipValue = (value: number) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ` ${currency}`;

  const chartData = supplierData.map((row) => ({
    ...row,
    totalPaid: row.totalPaid ?? 0,
    balance: row.balance ?? 0,
  }));

  if (chartData.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
        <Typography variant="body2">{t('common:dashboard.noData')}</Typography>
      </Box>
    );
  }

  return (
    <DashboardSizedChart height={280} dynamicHeight={supplierData.length * 38}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ left: 10, right: 20, top: 5, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis type="number" tickFormatter={formatCurrency} />
        <YAxis
          type="category"
          dataKey="supplier"
          width={120}
          tick={{ fontSize: 11 }}
          tickFormatter={(v: string) => v.length > 18 ? v.substring(0, 16) + '…' : v}
        />
        <Tooltip
          formatter={(value: number, name: string) => [
            name === 'itemCount' ? value : formatTooltipValue(value),
            name === 'totalSupplied'
              ? t('common:dashboard.suppliers.volume')
              : name === 'totalPaid'
                ? t('common:dashboard.suppliers.paid')
                : name === 'balance'
                  ? t('common:dashboard.suppliers.balance')
                  : t('common:dashboard.suppliers.items'),
          ]}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="totalSupplied" fill="#00695c" radius={[0, 4, 4, 0]} name="totalSupplied" />
        <Bar dataKey="totalPaid" fill="#0288d1" radius={[0, 4, 4, 0]} name="totalPaid" />
        <Bar dataKey="balance" fill="#d32f2f" radius={[0, 4, 4, 0]} name="balance" />
      </BarChart>
    </DashboardSizedChart>
  );
};

const SupplierPerformance = () => {
  const { supplierData, sectionLoading } = useDashboard();
  const { t } = useTranslation();

  if (sectionLoading.activity) {
    return (
      <DashboardWidget title={t('common:dashboard.suppliers.title')} helpKey="suppliers.title" chartHeight={380}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget
      title={t('common:dashboard.suppliers.title')}
      helpKey="suppliers.title"
      chartHeight={Math.max(280, supplierData.length * 38)}
      scrollable
    >
      <SupplierPerformanceBody />
    </DashboardWidget>
  );
};

export default SupplierPerformance;